import { useEffect, useState } from 'react';
import {
  collection,
  onSnapshot,
  orderBy,
  query,
  type DocumentData,
  type Query,
} from 'firebase/firestore';
import type { BacklogSuggestionDoc } from '@mct/shared';
import { getDb } from '../lib/firebase';

/**
 * Single subscription to every suggestion in the project. Filtering by
 * status / snooze is done client-side (see `filterSuggestions`) — the
 * volume is bounded by unique Backlog issues, and we need the full set
 * anyway to render the pending-count badge on the tab.
 */
export function useSuggestions(projectId: string | null): {
  data: BacklogSuggestionDoc[];
  loading: boolean;
  error: Error | null;
} {
  const [q, setQ] = useState<Query<DocumentData> | null>(null);
  const [data, setData] = useState<BacklogSuggestionDoc[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!projectId) {
      setQ(null);
      setData([]);
      setLoading(false);
      return;
    }
    setQ(
      query(
        collection(getDb(), 'projects', projectId, 'backlogSuggestions'),
        orderBy('createdAt', 'desc'),
      ),
    );
  }, [projectId]);

  useEffect(() => {
    if (!q) return;
    setLoading(true);
    const unsub = onSnapshot(
      q,
      (snap) => {
        setData(snap.docs.map((d) => d.data() as BacklogSuggestionDoc));
        setLoading(false);
      },
      (err) => {
        setError(err);
        setLoading(false);
      },
    );
    return () => unsub();
  }, [q]);

  return { data, loading, error };
}
